import { AccessTime } from '@mui/icons-material';
import { Stack, Theme } from '@mui/material';
import { Dayjs } from 'dayjs';
import React from 'react';
import { formatDate, getRelativeTime, isBeforeToday } from '../../../../../../util/date';
import { getStatusColor } from '../../../../constants/colors';
import { Todo } from '../../../../types/types';
import { EditTextField, StyledTextNoSelect } from '../../style/styles';

interface TodoContentProps {
    todo: Todo;
    isEditing: boolean;
    editText: string;
    onEditTextChange: (text: string) => void;
    onKeyPress: (e: React.KeyboardEvent) => void;
    onBlur: () => void;
    inputRef: React.RefObject<HTMLInputElement>;
    onContextMenu: (event: React.MouseEvent<HTMLDivElement>) => void;
}

const TodoContent: React.FC<TodoContentProps> = ({
    todo,
    isEditing,
    editText,
    onEditTextChange,
    onKeyPress,
    onBlur,
    inputRef,
    onContextMenu,
}) => {
    const getDueDateColor = (dueDate: Dayjs | undefined, theme: Theme) => {
        if (todo.completed) return getStatusColor(theme).completed;
        if (isBeforeToday(dueDate?.toString() || '')) return getStatusColor(theme).overdue;
        return 'text.secondary';
    };

    return (
        <Stack
            flexGrow={1}
            onContextMenu={onContextMenu}
            minWidth={0}
            overflow="hidden"
            justifyContent="center"
        >
            {isEditing ? (
                <EditTextField
                    fullWidth
                    multiline
                    value={editText}
                    onChange={(e) => onEditTextChange(e.target.value)}
                    onKeyDown={onKeyPress}
                    onBlur={onBlur}
                    inputRef={inputRef}
                    size="small"
                    sx={{
                        backgroundColor: 'background.paper',
                    }}
                />
            ) : (
                <Stack spacing={0.5} justifyContent="center">
                    <StyledTextNoSelect
                        sx={{
                            textDecoration: todo.completed ? 'line-through' : 'none',
                            color: todo.completed ? 'text.secondary' : 'text.primary',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}
                    >
                        {todo.text}
                    </StyledTextNoSelect>

                    {todo.dueDate && (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                        >
                            <AccessTime
                                sx={{
                                    fontSize: '0.875rem',
                                    color: theme => getDueDateColor(todo.dueDate, theme)
                                }}
                            />
                            <StyledTextNoSelect
                                variant="caption"
                                sx={{
                                    color: theme => getDueDateColor(todo.dueDate, theme)
                                }}
                            >
                                {formatDate(todo.dueDate.toString())} | {getRelativeTime(todo.dueDate.toString())}
                            </StyledTextNoSelect>
                        </Stack>
                    )}
                </Stack>
            )}
        </Stack>
    );
};

export default TodoContent; 